import type { Ssm5DiagramModule, Ssm5DtcModule, Ssm5Session } from '@packages/domain/ssm5';

import { colors } from '@packages/core/styles';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';

import type { ModuleSelection } from '../helpers/diagram-modules';
import type { DiagramNames } from '../helpers/diagram-svg';
import type { Ssm5DtcFilterKey, Ssm5DtcFilters } from '../helpers/dtc-filters';
import type { Ssm5FreezeFramesTarget } from '../helpers/ssm5-paths';

import { selectedDiagramModule } from '../helpers/diagram-modules';
import { readDiagramNames } from '../helpers/diagram-svg';
import { ALL_DTC_FILTERS_ON } from '../helpers/dtc-filters';
import { ssm5SessionPath } from '../helpers/ssm5-paths';
import { useSsm5Diagram } from '../hooks/use-ssm5-diagram';
import { useSsm5Dtcs } from '../hooks/use-ssm5-dtcs';
import { Ssm5CanBusDiagram } from './ssm5-can-bus-diagram';
import { Ssm5ConnectedPanel } from './ssm5-connected-panel';
import { Ssm5FreezeFramesPage } from './ssm5-freeze-frames-page';
import { Ssm5LoaderBar } from './ssm5-loader-bar';
import { Ssm5ModulePanel } from './ssm5-module-panel';
import { Ssm5ModuleStatusLegend } from './ssm5-module-status-legend';
import { Ssm5PanelMessage } from './ssm5-panel-message';
import { Ssm5StatusPanel } from './ssm5-status-panel';

/** Props for {@link Ssm5SessionBody}. */
export type Ssm5SessionBodyProps = {
    /** The DTC whose FFD detail page is open, or undefined for the strip and DTC panel. */
    freezeFramesTarget?: Ssm5FreezeFramesTarget;
    /** Whether the session is still open; a lost one keeps its last read. */
    isConnected: boolean;
    /** Receives a press of the panel's reconnect action, offered after a DTC read failure no retry recovers from. */
    onReconnect: () => void;
    /** The session. */
    session: Ssm5Session;
};

/** The modules of a diagram that is not loaded. */
const NO_MODULES: readonly Ssm5DiagramModule[] = [];

/** The modules of a Status and DTCs list that is not loaded. */
const NO_LIST_MODULES: readonly Ssm5DtcModule[] = [];

/** The module names of a diagram that is not loaded. */
const NO_NAMES: ReadonlyMap<string, DiagramNames> = new Map();

const useStyles = createUseStyles({
    // The drawing's box, so the loader of a DTC read made while the drawing is there can lie over it.
    drawing: {
        position: 'relative',
    },
    // The column scrolls on its own, so a diagram taller than the view never grows the page. The strip
    // keeps the page inset; the legend and the diagram run edge to edge under it, as the design draws them.
    main: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        overflowY: 'auto',
    },
    // The loader of a DTC read made again lies over the top of the drawing, on white, so the drawing
    // never moves while the DTCs are read; the first read shows it in the room the drawing takes later.
    rereading: {
        backgroundColor: colors.white,
        display: 'flex',
        left: 0,
        padding: '24px 0',
        position: 'absolute',
        right: 0,
        top: 0,
    },
    strip: {
        padding: 24,
    },
    // The message of a vehicle without a drawing fills the diagram area under the legend, centered in it.
    unavailable: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
    },
});

/**
 * The page body of an open or lost SSM5 session: the session strip over the reading loader, the module
 * status legend and the CAN Bus diagram, next to the Status and DTCs panel. A vehicle without a drawing,
 * or a diagram read that failed, gets a message in place of the diagram; the modules the backend reported
 * keep opening from their cards. The DTCs and the diagram are read once per session, and the last reads
 * stay when the connection is lost; a DTC read made again with the drawing there shows its loader over
 * the drawing. A module selected on the diagram, or opened from its card in the list, takes the panel
 * over until its back button is pressed, which also clears the selection: the panel shows the module's
 * abbreviation as its cell shows it and its full name as the backend spells it; a card whose system the
 * diagram has no module for opens what the list knows of it, without a name. The FFD page replaces the
 * strip and panel while a target is open,
 * keeping the reads, the filters and the open module. Returning through the event detail action reads
 * the DTCs and the diagram again only while connected.
 *
 * @param props - {@link Ssm5SessionBodyProps}
 * @returns the session body
 */
export function Ssm5SessionBody({ freezeFramesTarget, isConnected, onReconnect, session }: Ssm5SessionBodyProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const dtcs = useSsm5Dtcs(session.cloudConnectorId);
    const diagram = useSsm5Diagram(session.cloudConnectorId);
    const navigate = useNavigate();
    const [filters, setFilters] = useState<Ssm5DtcFilters>(ALL_DTC_FILTERS_ON);
    const [selection, setSelection] = useState<ModuleSelection>();
    const loadedDiagram = diagram.state.status === 'loaded' ? diagram.state.diagram : undefined;
    const modules = loadedDiagram?.modules ?? NO_MODULES;
    const listModules = dtcs.state.status === 'loaded' ? dtcs.state.readout.modules : NO_LIST_MODULES;
    const drawing = loadedDiagram?.svg;
    const isDiagramUnavailable =
        diagram.state.status === 'failed' || (loadedDiagram !== undefined && drawing === undefined);
    const names = useMemo(() => (drawing === undefined ? NO_NAMES : readDiagramNames(drawing)), [drawing]);
    const selectedModule = selectedDiagramModule(selection, modules, listModules);
    const isReported = selectedModule !== undefined && modules.includes(selectedModule);
    const switchFilter = useCallback(
        (key: Ssm5DtcFilterKey, isChecked: boolean) => setFilters((current) => ({ ...current, [key]: isChecked })),
        []
    );
    const selectModule = useCallback((id: string) => setSelection({ id }), []);
    const openModuleBySystem = useCallback((system: string) => setSelection({ system }), []);
    const closeModule = useCallback(() => setSelection(undefined), []);
    const returnToDtcs = useCallback(() => {
        navigate(ssm5SessionPath(session.vin));

        if (isConnected) {
            dtcs.reload();
            diagram.reload();
        }
    }, [diagram, dtcs, isConnected, navigate, session.vin]);

    const loader = dtcs.state.status === 'loading' && (
        <Ssm5LoaderBar message={t('ssm5:dtcs.reading')} percent={dtcs.state.progress} />
    );

    if (freezeFramesTarget !== undefined) {
        return (
            <Ssm5FreezeFramesPage
                canRetry={isConnected}
                key={`${freezeFramesTarget.module}:${freezeFramesTarget.dtcCode}`}
                onReturnToDtcs={returnToDtcs}
                session={session}
                target={freezeFramesTarget}
            />
        );
    }

    return (
        <>
            <div className={classes.main}>
                <div className={classes.strip}>
                    <Ssm5ConnectedPanel isConnected={isConnected} session={session} />
                </div>
                <Ssm5ModuleStatusLegend />
                {drawing === undefined && loader}
                {drawing !== undefined && (
                    <div className={classes.drawing}>
                        <Ssm5CanBusDiagram
                            modules={modules}
                            onSelect={selectModule}
                            selectedModuleId={isReported ? selectedModule.id : undefined}
                            svg={drawing}
                        />
                        {loader && <div className={classes.rereading}>{loader}</div>}
                    </div>
                )}
                {isDiagramUnavailable && (
                    <div className={classes.unavailable}>
                        <Ssm5PanelMessage
                            description={t('ssm5:diagram.unavailable.description')}
                            icon="account-tree"
                            title={t('ssm5:diagram.unavailable.title')}
                            variant="page"
                        />
                    </div>
                )}
            </div>
            {selectedModule ? (
                <Ssm5ModulePanel
                    filters={filters}
                    key={selectedModule.id}
                    label={names.get(selectedModule.id)?.label ?? selectedModule.id}
                    module={selectedModule}
                    name={isReported ? selectedModule.name : undefined}
                    onBack={closeModule}
                    onFilterChange={switchFilter}
                    supplyVoltage={loadedDiagram?.supplyVoltage}
                    tripTime={dtcs.state.status === 'loaded' ? dtcs.state.readout.tripTime : undefined}
                    vin={session.vin}
                />
            ) : (
                <Ssm5StatusPanel
                    canRetry={isConnected}
                    dtcs={dtcs}
                    filters={filters}
                    onFilterChange={switchFilter}
                    onOpenModule={openModuleBySystem}
                    onReconnect={onReconnect}
                    vin={session.vin}
                />
            )}
        </>
    );
}
